import { zodResolver } from '@hookform/resolvers/zod';
import { Input } from '@platform/design-system';
import { FormProvider, useForm } from 'react-hook-form';
import { FormSheet } from '../FormSheet.tsx';
import type { Subsidiary } from '../settings/subsidiaries.ts';
import { usePageContext } from '../usePageContext.ts';
import { AccessFields } from './AccessFields.tsx';
import {
  accessFormOf,
  invitationBodyOf,
  invitationFormSchema,
  type InvitationBody,
  type InvitationFormValues,
} from './team.ts';
import styles from './Team.module.css';

export type InviteSheetProps = {
  subsidiaries: readonly Subsidiary[];
  isAdmin: boolean;
  submitting: boolean;
  errorMessage: string | null;
  onClose: () => void;
  onSubmit: (body: InvitationBody) => void;
};

export const InviteSheet = ({
  subsidiaries,
  isAdmin,
  submitting,
  errorMessage,
  onClose,
  onSubmit,
}: InviteSheetProps) => {
  const { t } = usePageContext();
  const form = useForm<InvitationFormValues>({
    resolver: zodResolver(invitationFormSchema),
    defaultValues: { ...accessFormOf(null), email: '', firstName: '', lastName: '' },
    mode: 'onBlur',
  });
  const { errors } = form.formState;

  return (
    <FormSheet
      open
      formId="invite-form"
      title={t('team.inviteTitle')}
      submitLabel={t('team.inviteSubmit')}
      submitting={submitting}
      errorMessage={errorMessage}
      onClose={onClose}
      onSubmit={(event) => {
        void form.handleSubmit((values) => {
          onSubmit(invitationBodyOf(values));
        })(event);
      }}
    >
      <FormProvider {...form}>
        <p className={styles['note']}>{t('team.inviteHelp')}</p>
        <Input
          label={t('team.email')}
          type="email"
          autoComplete="off"
          dir="ltr"
          required
          errorText={errors.email ? t('team.emailInvalid') : undefined}
          {...form.register('email')}
        />
        <Input
          label={t('team.firstName')}
          required
          errorText={errors.firstName ? t('team.firstNameRequired') : undefined}
          {...form.register('firstName')}
        />
        <Input label={t('team.lastName')} {...form.register('lastName')} />
        <AccessFields subsidiaries={subsidiaries} isAdmin={isAdmin} />
      </FormProvider>
    </FormSheet>
  );
};
